<template>
  <div
    class="epg-grid-view"
    :class="{ 'epg-grid-view--static': !opts.showScrollableHeader }"
  >
    <div class="epg-grid-nav js-epg-nav">
      <div class="epg-header-bar">
        <ui-epg-calendar
          :epg-time="epgTime"
          :with-dates="true"
        ></ui-epg-calendar>
        <ui-epg-view-switch v-if="opts.showLayoutSwitch"></ui-epg-view-switch>
      </div>

      <div class="epg-timeline-scroll js-epg-timeline-scroll">
        <div class="epg-timeline-scroll-drag js-epg-timeline-scroll-drag"></div>
      </div>

      <div class="epg-timeline">
        <i
          class="o-icon o-icon--left o-icon--bold o-typography--action epg-timeline__arrow "
          :class="{ 'u-hidden': !showLeftArrow }"
          @mousedown.stop="scrollLeft"
          @mouseup="stopScrollAnimation"
          @mouseout="stopScrollAnimation"
        ></i>
        <i
          class="o-icon o-icon--right o-icon--bold o-typography--action epg-timeline__arrow epg-timeline__arrow--right"
          :class="{ 'u-hidden': !showRightArrow }"
          @mousedown.stop="scrollRight"
          @mouseup="stopScrollAnimation"
          @mouseout="stopScrollAnimation"
        ></i>
        <div
          ref="timeline"
          class="epg-timeline-wrapper"
        >
          <div
            v-show="dom.marker !== undefined && !loading"
            class="epg-marker-time"
            :style="markerStyle"
          >
            <span>{{ epgTime.now.format('HH:mm') }}</span>
            <div class="bottom-fill"></div>
          </div>
          <template v-if="channels">
            <div
              v-for="hour in hours"
              class="epg-hour"
              :style="{ width: (dom.hourWidth / 2) + 'px' }"
            >
              <span>{{ hour }}</span><i class="markers"></i>
            </div>
          </template>
        </div>
      </div>
      <div
        v-if="isNowButtonVisible"
        class="o-button o-button--primary o-button--dense epg-grid-nav__live-button"
        @click.prevent="goToNow"
      >
        <span class="o-typography__label o-button__label o-button__live">
          {{ $t('glossary.nowLive') }}
        </span>
      </div>
    </div>

    <div
      v-if="loading"
      class="epg-loader"
    ></div>
    <div class="epg-grid-holder js-epg-grid-holder">
      <div class="epg-channels js-epg-channels">
        <div
          v-for="channel in selectedChannels"
          class="epg-channel"
        >
          <div class="epg-channel__box">
            <router-link
              :to="channel | link"
              class="channel-logo u-clickable"
            >
              <v-badge
                :product="channel"
                class="epg-channel__badge"
              ></v-badge>
              <v-product-image
                :product="channel"
                :type="ImageType.SQUARE_MINI"
              ></v-product-image>
            </router-link>
          </div>
        </div>
      </div>

      <div
        class="epg-grid-wrapper js-epg-grid-wrapper"
        @mouseover="showPopover"
        @mouseout="hidePopover"
      >
        <div class="epg-grid js-epg-grid">
          <div
            v-show="epgTime.dayOffset === 0 && dom.marker !== undefined"
            class="epg-marker"
            :style="markerStyle"
          ></div>
          <template v-for="chunk in chunks">
            <div
              v-if="chunk.loaded"
              :class="{ chunk: true }"
              class="epg-chunk"
              :style="chunk.style"
            >
              <div
                v-for="(channel, index) in chunk.channels"
                class="epg-chunk-channel chunk-channel"
              >
                <template v-if="channel.programs.length">
                  <v-epg-grid-item
                    v-for="program in channel.programs"
                    :key="program.id"
                    :filter="filter"
                    :program-id="program.id"
                    :program="program"
                    :index="index"
                    :time="epgTime"
                    :dom="dom"
                    :chunk="chunk"
                    :channel="channel"
                    :canvas-width="dom.hourWidth * dom.hoursPerCanvas"
                  ></v-epg-grid-item>
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <ui-epg-popover
      v-if="selectedChannels.length"
      ref="popover"
      :channels="channels"
    ></ui-epg-popover>
  </div>
</template>
<script>
  import dayjs from 'dayjs';
  import { ImageType } from '@/shared/enums';
  import epgTime from '@/shared/libs/time';
  import { link } from '@/index/shared/filters';
  import { fillProgramsMixin } from '@/shared/mixins';
  import { epgFavouriteMixin, epgPopoverMixin } from '@/index/shared/mixins';
  import { VBadge } from '@/index/shared/components';
  import { carouselEPGSchema } from '@/shared/utils/seo';
  import EpgDom from '../dom';
  import DomEvent from '../dom/dom-event';
  import UiEpgPopover from '../popover.vue';
  import UiEpgCalendar from '../calendar.vue';
  import UiEpgViewSwitch from '../epg-view-switch.vue';
  import VEpgGridItem from './epg-grid-item.vue';
  import VProductImage from '../../product-image/index.vue';

  export default {
    name: 'EpgGrid',
    components: {
      VBadge,
      VEpgGridItem,
      VProductImage,
      UiEpgCalendar,
      UiEpgPopover,
      UiEpgViewSwitch,
    },
    enums: {
      ImageType,
    },
    filters: {
      link,
    },
    mixins: [
      fillProgramsMixin,
      epgPopoverMixin,
      epgFavouriteMixin,
    ],
    props: {
      channels: {
        type: Array,
        default: () => [],
      },
      categories: {
        type: Array,
        default: () => [],
      },
      opts: {
        type: Object,
        default: () => {
          return {};
        },
      },
    },
    data() {
      return {
        dom: {},
        epgTime,
        dayjs,
        hours: [],
        marker: undefined,
        chunks: [],
        chunkLoaders: [],
        position: {},
        days: [],
        initialized: false,
        interval: null,
        start: undefined,
        stop: undefined,
        mask: {},
        slider: undefined,
        loading: false,
        isEpgLoaded: false,
        filter: undefined,
        showLeftArrow: true,
        showRightArrow: true,
      };
    },
    computed: {
      isNowButtonVisible() {
        if (!this.opts.showReturnToNow || !this.selectedChannels.length) {
          return false;
        }

        if (this.epgTime.dayOffset) {
          return true;
        }

        return !this.dom.isInLiveRange;
      },
      currentDay() {
        return this.epgTime.today;
      },
      markerStyle() {
        return { transform: `translate(${this.dom.marker}px, 0)` };
      },
    },
    watch: {
      channels(val, old) {
        if (val === old) {
          return;
        }

        this.reset();
      },
      'epgTime.now': function (newVal, oldVal) {
        if (this.epgTime.dayOffset === 0) {
          this.dom.updateMarker();

          if (newVal.startOf('day').diff(oldVal.startOf('day'), 'days') > 0) {
            this.goToNow();
            this.reset();
          }
        }
      },
      'epgTime.dayOffset': function () {
        this.reset();
      },
    },
    mounted() {
      this.epgTime.updateWithInterval(this.$store.state.config.epgUpdateInterval);
      this.init();

      this.dom.on(DomEvent.LEFT_EDGE_REACHED, this.hideLeftArrow);
      this.dom.on(DomEvent.RIGHT_EDGE_REACHED, this.hideRightArrow);
      this.dom.on(DomEvent.NO_EDGE_REACHED, this.showBothArrows);
      this.dom.on(DomEvent.DRAG_START, this.onDragStart);
      this.dom.on(DomEvent.DRAG_STOP, this.onDragStop);
    },
    beforeDestroy() {
      this.unbind();
      this.epgTime.stopUpdate();
    },
    methods: {
      init() {
        this.hours = [];
        for (let i = 0; i < 24; i++) {
          this.hours.push(this.getHour(i) + ':00');
          this.hours.push(this.getHour(i) + ':30');
        }

        this.dom = new EpgDom(this.$el, {
          grid: window,
          $window: this.$window,
          timeline: this.$refs.timeline,
        });

        this.chunkLoaders = this.dom.chunkLoaders;

        this.dom.setup(this.channels).then(() => {
          this.update();
          this.dom.updateMask();
        });

        this.initialized = true;
      },
      unbind() {
        this.dom.unbind();
        this.dom.removeAllListeners();
      },
      changeDay(day) {
        this.epgTime.dayOffset = day.startOf('day').diff(this.epgTime.now.clone().startOf('day'), 'days');
      },
      reset() {
        if (!this.initialized || this.loading) {
          return;
        }

        this.dom.removeAllListeners('move');
        this.loading = true;
        this.chunks = [];
        this.update();
      },
      onViewport() {
        this.$nextTick(this.update);
      },
      update(viewport) {
        viewport = viewport || this.dom.viewport;
        this.createChunks(viewport);
      },
      scrollLeft() {
        this.animate('left');
        this.interval = setInterval(() => {
          this.animate('left');
        }, 100);
      },
      scrollRight() {
        this.animate('right');
        this.interval = setInterval(() => {
          this.animate('right');
        }, 100);
      },
      animate(direction) {
        if (direction === 'right') {
          this.dom.horizontalScrollRight();

          if (!this.showRightArrow) {
            this.stopScrollAnimation();
          }
        } else {
          this.dom.horizontalScrollLeft();

          if (!this.showLeftArrow) {
            this.stopScrollAnimation();
          }
        }
      },
      stopScrollAnimation() {
        clearInterval(this.interval);
      },
      createChunks(viewport) {
        for (let period = viewport.fromPeriod; period <= viewport.toPeriod; period++) {
          for (let channelSet = viewport.fromChannelSet; channelSet <= viewport.toChannelSet; channelSet++) {
            this.createChunk(period, channelSet);
          }
        }
      },
      createChunk(period, channelSet) {
        let chunk = this.chunkExist(period, channelSet);
        if (chunk) {
          return this.loadChunk(chunk);
        }

        const channels = this.getChannelsInSet(channelSet, this.selectedChannels);
        if (!channels.length) {
          return;
        }

        const day = this.epgTime.today;
        const range = {
          since: day.add(period * this.dom.hoursInDay, 'h').clone(),
          till: day.add(this.dom.hoursInDay, 'h'),
        };

        const offset = {
          top: channelSet * this.dom.channelHeight * this.dom.channelsInChunk,
          left: this.dom.getChunkLeftOffset(period),
        };

        chunk = {
          loaded: false,
          channelSet,
          channels,
          period,
          range,
          offset,
          style: {
            top: offset.top + 'px',
            left: offset.left + 'px',
          },
        };

        this.chunks.push(chunk);
        return this.loadChunk(chunk);
      },
      chunkExist(period, channelSet) {
        for (let i = 0, max = this.chunks.length; i < max; i++) {
          if (this.chunks[i].period === period && this.chunks[i].channelSet === channelSet) {
            return this.chunks[i];
          }
        }
        return false;
      },
      loadChunk(chunk) {
        if (chunk.loaded) {
          return Promise.resolve(chunk);
        }

        return this.fillPrograms(chunk.channels, chunk.range.since.valueOf(), chunk.range.till.valueOf() - 1)
          .then(() => {
            for (let i = 0, max = chunk.channels.length; i < max; i++) {
              const channel = this.manager.channels.collection.byId[chunk.channels[i].id];
              chunk.channels[i].programs = this.manager.getSince(this.epgTime, {
                since: chunk.range.since,
                till: chunk.range.till,
              }, channel);
            }

            chunk.loaded = true;
            this.loading = false;

            this.dom.updateMarkerPosition();
            this.dom.updateMask();
            if (!this.isEpgLoaded) {
              this.isEpgLoaded = true;
            }
            carouselEPGSchema(this.channels);
          });
      },
      getChannelsInSet(channelSet, channels) {
        const start = channelSet * this.dom.channelsInChunk;
        const end = start + this.dom.channelsInChunk;

        return channels.slice(start, end);
      },
      getHourWidth() {
        return this.dom.hourWidth + 'px';
      },
      getPrograms(channel, chunk) {
        return this.fillPrograms([channel], chunk.range.since, chunk.range.till);
      },
      getHour(hour) {
        return hour > 9 ? hour : '0' + hour;
      },
      goToNow() {
        this.epgTime.dayOffset = 0;
        this.dom.scrollToMarker();
      },
      getTimeClass(i) {
        return `time-${i}`;
      },
      play(channel, program) {
        if (program.catchupAvailable) {
          return this.$refs.vPlayer.load(program, 'CATCHUP');
        }

        if (program.isCurrent) {
          return this.$refs.vPlayer.load(channel, 'LIVE');
        }
      },
      updateFilter(category = {}) {
        this.filter = category.id;
      },
      hideLeftArrow() {
        this.showLeftArrow = false;
        this.showRightArrow = true;
      },
      hideRightArrow() {
        this.showLeftArrow = true;
        this.showRightArrow = false;
      },
      showBothArrows() {
        this.showLeftArrow = true;
        this.showRightArrow = true;
      },
    },
  };
</script>
